import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import z from "zod"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { userUpdateProfileSchema } from "@tfg/types/schemas/user"
import { standardEmail } from "@tfg/types/schemas/utils"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { i18n } from "~/i18n"

export const loginSchema = z.object({
  username: standardEmail(),
  password: z.string(),
  recaptchaToken: z.string().optional(),
})

export type LoginForm = z.infer<typeof loginSchema>

export const useLogin = (
  options?: UseMutationOptions<LibraryResponses.Login, AxiosError, LoginForm>
) =>
  useMutation<LibraryResponses.Login, AxiosError, LoginForm>(
    ["auth", "login"],
    async (variables) => {
      const response = await httpClient.post<LibraryResponses.Login>(
        "auth/login/password",
        variables
      )
      return response.data
    },
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        await queryClient.invalidateQueries(["auth"])
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )

export const useForgotPasswordMutation = (
  options?: UseMutationOptions<void, AxiosError, { email: string }>
) =>
  useMutation<void, AxiosError, { email: string }>(
    ["auth", "forgotPassword"],
    async (variables) => {
      const response = await httpClient.post("auth/reset-password", variables)
      return response.data
    },
    {
      onSuccess: async (data, variables, context) => {
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )

export const useMagicLinkMutation = (
  options?: UseMutationOptions<void, AxiosError, { email: string }>
) =>
  useMutation<void, AxiosError, { email: string }>(
    ["auth", "magicLink"],
    async (variables) => {
      const response = await httpClient.post("auth/magic-link", variables)
      return response.data
    },
    {
      onSuccess: async (data, variables, context) => {
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )

const basePasswordSchema = z
  .string()
  .regex(/[a-z]/)
  .regex(/[A-Z]/)
  .regex(/[0-9]/)

export const userPasswordSchema = basePasswordSchema.min(12)
export const adminPasswordSchema = basePasswordSchema.min(16)

export const passwordSchema = z
  .object({
    password: userPasswordSchema,
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    message: i18n.t("profile.passwordsDoNotMatch", { ns: "account" }),
    path: ["confirm"],
  })

export type PasswordForm = z.infer<typeof passwordSchema>
export type ProfileForm = z.infer<typeof userUpdateProfileSchema>

export const useUpdateProfileMutation = (
  options?: UseMutationOptions<void, AxiosError, ProfileForm>
) => {
  return useMutation<void, AxiosError, ProfileForm>(
    ["auth", "profile"],
    async (variables) => {
      await httpClient.patch("users/profile", variables)
    },
    {
      onSuccess: async (data, variables, context) => {
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )
}

export const useUpdatePasswordMutation = (
  options?: UseMutationOptions<void, AxiosError, PasswordForm>
) => {
  return useMutation<void, AxiosError, PasswordForm>(
    ["auth", "profile"],
    async (variables) => {
      await httpClient.patch("users/password", variables)
    },
    {
      onSuccess: async (data, variables, context) => {
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )
}

export const twoFactorSchema = z.object({
  userId: z.string().optional(),
  token: z.string(),
})

export type TwoFactorForm = z.infer<typeof twoFactorSchema>

type ConfigureTwoFactorResponse = {
  url: string
}

export const useConfigureTwoFactorMutation = (
  options?: UseMutationOptions<ConfigureTwoFactorResponse, AxiosError, void>
) =>
  useMutation<ConfigureTwoFactorResponse, AxiosError, void>(
    ["auth", "config2fa"],
    async (variables) => {
      const response = await httpClient.post("auth/config2fa", variables)
      return response.data
    },
    {
      onSuccess: async (data, variables, context) => {
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )

export const useVerifyTwoFactorMutation = (
  options?: UseMutationOptions<
    LibraryResponses.TFGUser,
    AxiosError,
    TwoFactorForm
  >
) =>
  useMutation<LibraryResponses.TFGUser, AxiosError, TwoFactorForm>(
    ["auth", "verify2fa"],
    async (variables) => {
      const response = await httpClient.post("auth/verify2fa", variables)
      return response.data
    },
    {
      onSuccess: async (data, variables, context) => {
        await options?.onSuccess?.(data, variables, context)
      },
    }
  )
